import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { setAuthHint } from "@/lib/authHint";
import { StockailySymbol } from "@/components/brand/StockailyBrand";
import { consumeGoogleReturnPath, safeNextPath } from "@/lib/safeNext";

/**
 * AuthCallback — Slice 1 (2026-02-14).
 *
 * Consumes the Emergent Google OAuth session_id from the URL fragment,
 * exchanges it for a Stockaily HttpOnly session_token cookie, then
 * routes the visitor to the intended destination.
 *
 * The false "Google login failed" toast that visitors hit in
 * production had two contributing causes:
 *   1. `/auth/google/session` was occasionally seen as failed even
 *      though the session cookie WAS written (e.g. a transient 500
 *      returned mid-response, a CSRF race on first-mount).  The old
 *      catch branch navigated straight to `/login?error=google`.
 *   2. When it navigated to /login the Login page fired the error
 *      toast before AuthContext had a chance to re-verify the session
 *      via /auth/me.
 *
 * The new flow keeps the exchange call as the primary path, but before
 * declaring failure we ask the backend the authoritative question via
 * checkAuth(): if /auth/me returns 200, the visitor IS authenticated
 * and we treat this callback as a success (setUser hydrated by
 * checkAuth, hint written, error param stripped, redirect to the
 * intended destination).  Only when the definitive check confirms
 * unauthed do we redirect to /login?error=google.
 *
 * The callback is idempotent: `hasProcessed.current` guards
 * double-execution under React Strict Mode, and every branch is safe
 * to re-enter without side-effects (setUser / setAuthHint / navigate
 * are all idempotent operations).
 *
 * No token, authorization code or session_id ever appears in any log
 * or user-visible message on this page.
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const fragment = window.location.hash || "";
    const m = fragment.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    // Slice-1-verification fix — strip the fragment from the visible
    // URL and browser history IMMEDIATELY, before any async work.
    // The provider necessarily returns the exchange handle in a URL
    // fragment (see doc-comment above), but leaving it in the address
    // bar is a hygiene issue: refresh, back/forward navigation, and
    // React Strict Mode's double-effect could each attempt to
    // reprocess the same handle.  Removing it synchronously ensures
    //   * only the first useEffect run sees a non-empty fragment,
    //   * a reload or ``history.back`` from a subsequent route
    //     cannot re-enter this callback with the same handle,
    //   * `hasProcessed.current` catches the Strict-Mode second
    //     invocation even if a browser preserved the fragment.
    // A refresh at this exact moment lands on ``/`` with an empty
    // fragment and the visitor is either already-authed (cookie set)
    // or on the login page — never in a broken re-exchange state.
    try {
      if (fragment) {
        window.history.replaceState(null, "", window.location.pathname + (window.location.search || ""));
      }
    } catch { /* swallow — best-effort address-bar cleanup */ }
    if (!sessionId) {
      navigate("/login");
      return;
    }

    // Resolve the intended destination BEFORE anything else so the
    // success and fallback branches share the same target.  Owner
    // console callers still use `__owner_return_after_google` for
    // backwards-compat.
    const resolveTarget = () => {
      try {
        if (window.sessionStorage.getItem("__owner_return_after_google") === "1") {
          window.sessionStorage.removeItem("__owner_return_after_google");
          return "/owner-console";
        }
      } catch { /* sessionStorage disabled */ }
      const stashed = consumeGoogleReturnPath();
      if (stashed) return safeNextPath(stashed, "/watchlist");
      return "/watchlist";
    };

    // Idempotent URL cleanup — strip the OAuth fragment AND any stale
    // ``?error=google`` left by a previous failed attempt so no stale
    // failure message ever surfaces after a successful callback.
    const cleanUrl = () => {
      try {
        const cleanSearch = (() => {
          const p = new URLSearchParams(window.location.search || "");
          p.delete("error");
          const s = p.toString();
          return s ? `?${s}` : "";
        })();
        window.history.replaceState(null, "", window.location.pathname + cleanSearch);
      } catch {
        window.history.replaceState(null, "", window.location.pathname);
      }
    };

    // Definitive fallback check: ask /auth/me directly.  Returns true
    // iff the visitor really is authenticated according to the
    // backend.  Any error / 401 / network failure returns false so the
    // caller falls through to the failure branch.
    const verifyFinalSession = async () => {
      try {
        const res = await api.get("/auth/me");
        if (res?.data?.user) {
          setAuthHint();
          setUser(res.data.user);
          return true;
        }
      } catch { /* fall through */ }
      return false;
    };

    (async () => {
      const target = resolveTarget();
      try {
        const res = await api.post("/auth/google/session", { session_id: sessionId });
        // WS-8 — server sets the HttpOnly session_token cookie; no
        // JS-readable credential is written.  Set a non-secret UI hint
        // and hydrate the auth context in-place BEFORE navigating so
        // the destination renders authenticated on its first paint.
        if (res?.data?.user) {
          setAuthHint();
          setUser(res.data.user);
        }
        // Force /auth/me to re-run so any downstream consumer that
        // reads through checkAuth() sees the freshly established
        // session on the very next tick.  Best-effort — a failure
        // here is not fatal because setUser already hydrated the
        // visible auth state.
        try {
          if (typeof checkAuth === "function") await checkAuth();
        } catch { /* ignore — setUser already hydrated */ }
        cleanUrl();
        navigate(target, { replace: true });
      } catch (postErr) {
        // Slice 1 fix — before we declare failure, ask the backend the
        // authoritative question.  A partial-success case (cookie
        // established, but the POST response was mis-classified as
        // failed by the SPA-fallback guard, or a transient 500 after
        // the cookie was written) is common enough in production that
        // we MUST NOT dead-end the visitor with a false failure toast.
        const authed = await verifyFinalSession();
        if (authed) {
          cleanUrl();
          navigate(target, { replace: true });
          return;
        }
        // Genuine failure — leave the visitor logged-out and surface
        // the error on the login page.  No token / code / session_id
        // ever ends up in the URL or logs.
        navigate("/login?error=google");
      }
    })();
  }, [navigate, setUser, checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center text-muted-foreground bg-background">
      <div className="text-center flex flex-col items-center gap-4">
        <div className="animate-pulse">
          <StockailySymbol height={48} decorative />
        </div>
        <div>
          <div className="font-display text-lg text-foreground">Signing you in…</div>
          <div className="text-sm mt-1">Verifying your Google session.</div>
        </div>
      </div>
    </div>
  );
}
